import { IconType } from "@jsq/ds/icons";
import { type PaymentAccountType, type RiskRating } from "~/graphql/supergraphTypes";
import { RiskRatingInfo } from "~/pages/Compliance/riskRating/types";
import { OnboardingCase } from "../../types";
import { RiskRatingType } from "../../utils/riskRatingCalculations";

/**
 * Available changes for Risk Ratings
 * - DECREASE: Single down arrow
 * - DECREASE_TWO: Double down arrow
 * - INCREASE: Up arrow
 * - UNCHANGED: Long dash
 * - MANUAL: Manual change
 */
export enum RiskType {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
}

export enum ChangeType {
  DECREASE = "decrease",
  DECREASE_TWO = "decrease2",
  INCREASE = "increase",
  UNCHANGED = "unchanged",
  MANUAL = "manual",
}

// Flattened structure for all risk changes
export const RISK_CHANGES = {
  DECREASE: ChangeType.DECREASE,
  DECREASE_TWO: ChangeType.DECREASE_TWO,
  INCREASE: ChangeType.INCREASE,
  UNCHANGED: ChangeType.UNCHANGED,
  MANUAL: ChangeType.MANUAL,
} as const;

// Type helper for TypeScript
export type RiskChange = (typeof RISK_CHANGES)[keyof typeof RISK_CHANGES] | null;

/**
 * Combined Risk Status Types
 * Format: {risk-level}-{change-type}
 * Example: low-decrease, medium-increase, high-unchanged
 */
export enum RiskStatusType {
  // Low Risk Statuses
  LOW_NONE = "low-none",
  LOW_PENDING = "low-pending",
  LOW_DECREASE = "low-decrease",
  LOW_DECREASE_TWO = "low-decrease2",
  LOW_UNCHANGED = "low-unchanged",

  // Medium Risk Statuses
  MEDIUM_NONE = "medium-none",
  MEDIUM_DECREASE = "medium-decrease",
  MEDIUM_INCREASE = "medium-increase",
  MEDIUM_UNCHANGED = "medium-unchanged",
  MEDIUM_MANUAL = "medium-manual",

  // High Risk Statuses
  HIGH_NONE = "high-none",
  HALT_NONE = "halt-none",
  HIGH_INCREASE = "high-increase",
  HIGH_UNCHANGED = "high-unchanged",
  HIGH_DECREASE = "high-decrease",

  // Not Determined Status
  NOT_DETERMINED = "not-determined",

  // Not Applicable Status
  NOT_APPLICABLE = "not-applicable",

  // Select Payment Beneficiary Status
  SELECT_PAYMENT_BENEFICIARY = "select-payment-beneficiary",
}

// Risk Rating Status Types
export type ArrowChangeConfig = {
  type: "arrow-down" | "arrow-up";
  count?: 1 | 2;
  isDanger?: boolean;
};

export type DashChangeConfig = {
  showDash: true;
};

export type ManualChangeConfig = {
  showManual: true;
};

export type ChangeIndicatorConfig = ArrowChangeConfig | DashChangeConfig | ManualChangeConfig;

export interface PaymentAccountInfo {
  bankCountry?: {
    name?: string;
  };
}

export interface ProfileInfo {
  id?: number;
  riskRating?: RiskRatingInfo;
  paymentAccounts?: PaymentAccountInfo[];
  parentRelationships?: Array<{
    id: string;
    relationshipType: string;
  }>;
}

export interface RiskRatingProps {
  status: RiskStatusType;
  data?: {
    complianceCase?: {
      investingProfile?: {
        addresses?: Array<{
          country?: {
            alpha2code?: string;
            name?: string;
          };
        }>;
        riskRating?: RiskRatingInfo;
      };
    };
    linkedProfile?: {
      riskRating?: RiskRatingInfo;
      paymentAccounts?: PaymentAccountInfo[];
    };
    linkedOnboardingCase?: {
      profiles?: ProfileInfo[];
      investingProfile?: ProfileInfo;
      paymentAccount?: PaymentAccountType;
    };
    simulatedRiskRating?: {
      paymentBeneficiaryRiskRating?: RiskRating;
      investorProfileRiskRating?: RiskRating;
    };
  };
}

export interface StatusConfig {
  containerClass: string;
  icon?: {
    type: "gauge-simple-min" | "gauge-simple-low" | "gauge-simple-high" | "circle";
    className: string;
  };
  text: {
    content: string;
    className: string;
  };
}

export interface ArrowIndicatorProps {
  config: ArrowChangeConfig;
  status: RiskStatusType;
}

export interface DashIndicatorProps {
  status: RiskStatusType;
}

export interface ChangeIndicatorProps {
  status: RiskStatusType;
}

export interface RiskRatingData {
  name: string;
  existing: {
    country: string | null;
    risk: RiskRating | null;
  };
  proposed: {
    country: string | null;
    risk: RiskRating | null;
    changeIndicator?: IconType;
  };
}

export type NewRiskRatingData = {
  linkedOnboardingCase: OnboardingCase | null | undefined;
  currentPaymentAccount: PaymentAccountType;
  newBeneficiaryRiskRating: RiskRatingType | null | undefined;
  existingBeneficiaryRiskRating: RiskRatingType | null | undefined;
};

export interface RiskRatingStatusProps extends RiskRatingProps {
  status: RiskStatusType;
  data?: {
    complianceCase?: {
      investingProfile?: {
        addresses?: Array<{
          country?: {
            alpha2code?: string;
            name?: string;
          };
        }>;
        riskRating?: RiskRatingInfo;
      };
    };
    linkedProfile?: {
      riskRating?: RiskRatingInfo;
      paymentAccounts?: PaymentAccountInfo[];
    };
    linkedOnboardingCase?: {
      profiles?: ProfileInfo[];
      investingProfile?: ProfileInfo;
    };
    simulatedRiskRating?: {
      paymentBeneficiaryRiskRating?: RiskRating;
      investorProfileRiskRating?: RiskRating;
    };
  };
  picrRevampEnabled?: boolean;
  newData?: NewRiskRatingData;
  currentRiskRating?: string;
  newRiskRating?: string;
  positionName?: string;
}
